import React, { useState, useRef, useEffect } from 'react';

export default function BulkCardMedia({ listing, height = 180, getMediaUrl, onDetailClick }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef(null);

  const mediaList = Array.isArray(listing.media) ? listing.media : [];
  const hasMultiple = mediaList.length > 1;

  const currentMedia = mediaList[activeIndex];

  // Auto-play / pause video when hovered or when active slide changes
  useEffect(() => {
    if (currentMedia?.type === 'video' && videoRef.current) {
      if (isHovered) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0; // Reset to start
      }
    }
  }, [isHovered, activeIndex, currentMedia]);

  const handlePrev = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveIndex((prev) => (prev === 0 ? mediaList.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveIndex((prev) => (prev === mediaList.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (e, idx) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveIndex(idx);
  };

  if (mediaList.length === 0) {
    return (
      <div 
        onClick={onDetailClick}
        style={{
          height,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          background: '#f8fafc',
          borderBottom: '1px solid var(--border)',
          cursor: 'pointer',
          gap: 6
        }}
      >
        <span style={{ fontSize: 40, opacity: 0.18 }}>📦</span>
        <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 500 }}>No image yet</span>
      </div>
    );
  }

  return (
    <div 
      style={{ 
        height, 
        position: 'relative', 
        overflow: 'hidden', 
        background: '#fff', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        borderBottom: '1px solid var(--border)', 
        padding: 12,
        userSelect: 'none'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Clickable Media Main Area */}
      <div 
        onClick={onDetailClick}
        style={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          cursor: 'pointer'
        }}
      >
        {currentMedia.type === 'video' ? (
          <video
            ref={videoRef}
            src={getMediaUrl(currentMedia.url)}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain', 
              borderRadius: 6 
            }}
            muted
            loop
            playsInline
          />
        ) : (
          <img
            src={getMediaUrl(currentMedia.url)}
            alt={`${listing.title} - view ${activeIndex + 1}`}
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain', 
              display: 'block', 
              transition: 'transform 0.35s ease',
              borderRadius: 6
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          />
        )}
      </div>

      {/* Video Indicator Icon */}
      {currentMedia.type === 'video' && (
        <span 
          style={{ 
            position: 'absolute', 
            top: 10, 
            left: 10, 
            background: 'var(--gold)', 
            color: 'var(--char)', 
            fontSize: 9, 
            fontWeight: 800, 
            padding: '3px 8px', 
            borderRadius: 8,
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
            zIndex: 9
          }}
        >
          🎬 VIDEO PREVIEW
        </span>
      )}

      {/* Status Badge */}
      <span 
        style={{ 
          position: 'absolute', 
          top: 10, 
          right: 10, 
          background: listing.status === 'active' ? '#E8F5EE' : '#FEE2E2', 
          color: listing.status === 'active' ? '#1B5C3A' : '#DC2626', 
          fontSize: 9, 
          fontWeight: 800, 
          padding: '3px 8px', 
          borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          textTransform: 'uppercase',
          zIndex: 9
        }}
      >
        {listing.status || 'active'}
      </span>


      {/* Prev Arrow */}
      {hasMultiple && isHovered && (
        <button
          onClick={handlePrev}
          style={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid var(--border)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--green)',
            fontSize: 16,
            fontWeight: 'bold',
            cursor: 'pointer',
            zIndex: 10,
            padding: 0,
            lineHeight: 1,
            transition: 'background 0.15s, transform 0.1s'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
        >
          ‹
        </button>
      )}

      {/* Next Arrow */}
      {hasMultiple && isHovered && (
        <button
          onClick={handleNext}
          style={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid var(--border)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--green)',
            fontSize: 16,
            fontWeight: 'bold',
            cursor: 'pointer',
            zIndex: 10,
            padding: 0,
            lineHeight: 1,
            transition: 'background 0.15s, transform 0.1s'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
        >
          ›
        </button>
      )}

      {/* Media Indicator Dots */}
      {hasMultiple && (
        <div style={{ 
          position: 'absolute', 
          bottom: 8, 
          display: 'flex', 
          gap: 4, 
          background: 'rgba(255, 255, 255, 0.85)', 
          padding: '3px 8px', 
          borderRadius: 12,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          zIndex: 8
        }}>
          {mediaList.map((m, i) => (
            <button
              key={i}
              onClick={(e) => handleDotClick(e, i)}
              style={{
                width: i === activeIndex ? 12 : 5,
                height: 5,
                borderRadius: 3,
                background: i === activeIndex ? 'var(--green)' : '#cbd5e1',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                outline: 'none'
              }}
              title={`View ${m.type} ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
